import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  useGetDayPerformancesMutation,
  useGetDaysAvailabilityQueries,
} from '../services/viva';
import {
  AddOnAKForStatGroup,
  AddOnStatGroupsByFlowFlat,
  ADDON_EVENTS_WITH_TIME_CAPACITY,
  BosEvent,
  BosEventForAddOnStatGroup,
  Flow,
  StatGroup,
  ADDON_EVENTS_WITH_CAPACITY,
} from '../services/viva/constants';
import {
  actions,
  selectFirstTimeSlot,
  selectFlow,
  selectShopCartAddOnSelections,
  selectShopCartDate,
  selectShopCartDateTime,
  selectWizardCurrentStep,
  useAppDispatch,
  useAppSelector,
} from '../store';
import { BuyTicketsLayoutContext } from '../layouts/BuyTicketsLayout';

import { getDateString } from './format-date';
import { newRelic } from './new-relic';

import type { PerformanceResponseObject } from '../services/viva/types';

export type TPerformance = {
  ak?: string;
  available: number;
  quantity?: number;
  addOnIndex?: number;
  productAK: string;
  index?: number;
};

export const useCapacityAddon = () => {
  const { setIsAddOnLoading } = useContext(BuyTicketsLayoutContext);
  const [pendingPromises, setPendingPromises] = useState(0);
  const dispatch = useAppDispatch();

  const flow = useAppSelector(selectFlow);
  const currentStep = useAppSelector(selectWizardCurrentStep);
  const { day, month, year } = useAppSelector(selectShopCartDate);
  const shopCartDateTime = useAppSelector(selectShopCartDateTime);
  const shopCartAddons = useAppSelector(
    (state) => state.order.shopCart.addOnSelections,
  );
  const shopCartAddonsWithQty = useAppSelector(selectShopCartAddOnSelections);
  const firstTimeSlot = useAppSelector(selectFirstTimeSlot);

  const dayPerformanceMutation = useGetDayPerformancesMutation();

  const [localLoadingState, setLocalLoadingState] = useState(false);
  const [currentStepState, setCurrentStepState] = useState<number | null>(
    currentStep,
  );
  const [addOnQtyState, setAddOnQtyState] = useState(
    shopCartAddonsWithQty.length,
  );

  const addOns = useMemo(() => AddOnStatGroupsByFlowFlat[flow], [flow]);
  const dateString = useMemo(
    () => getDateString(year, month, day),
    [year, month, day],
  );

  const bosEvents = useMemo(
    () =>
      AddOnStatGroupsByFlowFlat[flow]
        ?.map((statGroup) => BosEventForAddOnStatGroup[statGroup])
        .filter((i) => !!i) as BosEvent[],
    [flow],
  );

  // Get day availability for all stat groups
  const dayAvailabilities = useGetDaysAvailabilityQueries({
    bosEvents,
    options: {
      enabled: true,
      sellable: false,
    },
  });

  const createPerformancePromises = useCallback(
    (addOns: StatGroup[]) =>
      addOns?.map<Promise<TPerformance>>(async (addOnStatGroup, index) => {
        const productAK = AddOnAKForStatGroup[addOnStatGroup];

        if (!productAK) {
          throw new Error(
            `AddOnAKForStatGroup needs to be updated with stat group ${addOnStatGroup}`,
          );
        }

        const bosEvent = BosEventForAddOnStatGroup[addOnStatGroup];
        const unavailablePerformance = {
          available: 0,
          productAK,
        };

        if (!bosEvent) {
          return Promise.resolve(unavailablePerformance);
        }

        const isTimedCapacityEvent =
          ADDON_EVENTS_WITH_TIME_CAPACITY.includes(bosEvent);
        const isCapacityEvent = ADDON_EVENTS_WITH_CAPACITY.includes(bosEvent);

        const availability = dayAvailabilities
          .find((a) => a.data?.bosEvent === bosEvent)
          ?.data?.availabilities.get(dateString);

        if (!availability) {
          return Promise.resolve(unavailablePerformance);
        }

        // If there is no availability, and the addon does not use an all day performance,
        // don't bother getting performances but keep array index order for Promise.all
        if (isTimedCapacityEvent && !availability?.available) {
          return Promise.resolve(unavailablePerformance);
        }

        const addOnIndex = shopCartAddons.findIndex((addOn) => {
          return addOn.ak === productAK;
        });
        const addOn = shopCartAddons[addOnIndex];

        const perfRes = await dayPerformanceMutation.mutateAsync({
          bosEvent,
          day,
        });

        let performanceResponseObject = perfRes.find(
          (perf) => perf.EVENTAK === bosEvent,
        );

        if (firstTimeSlot) {
          const targetTime = new Date(firstTimeSlot.timeRaw).getTime();

          const closestPastPerformance =
            perfRes.reduce<PerformanceResponseObject>((closest, current) => {
              const currentTime = new Date(current.TIME).getTime();
              const closestTime = new Date(closest.TIME).getTime();

              if (currentTime < targetTime && currentTime > closestTime) {
                return current;
              }
              return closest;
            }, perfRes[0] || ({} as PerformanceResponseObject));
          performanceResponseObject = closestPastPerformance;
        }
        if (!performanceResponseObject) {
          return Promise.resolve(unavailablePerformance);
        }

        return {
          ak: performanceResponseObject.AK,
          available:
            isTimedCapacityEvent || isCapacityEvent
              ? parseInt(perfRes[0]?.AVAILABILITY.AVAILABLE ?? '0')
              : 9999,
          quantity: addOn?.quantity ?? 0,
          addOnIndex,
          productAK,
          index,
        } as TPerformance;
      }),
    [firstTimeSlot, shopCartAddons],
  );

  const asyncEffect = useCallback(async () => {
    if (!addOns) {
      return;
    }

    setIsAddOnLoading(true);
    setLocalLoadingState(true);
    setPendingPromises((prevCount) => prevCount + 1);

    try {
      const performancePromises = createPerformancePromises(addOns);
      const performanceResponses = await Promise.all(performancePromises);
      const performancesHash = Object.fromEntries(
        performanceResponses.filter((p) => !!p).map((p) => [p!.productAK, p!]),
      );

      newRelic.logCartAction('useCapacityAddon', 'setAddOnsPerformancesHash', {
        performancesHash,
      });

      dispatch(actions.order.setAddOnsPerformancesHash(performancesHash));
    } catch (err) {
      console.error(err);
    } finally {
      setIsAddOnLoading(false);
      setLocalLoadingState(false);
      setPendingPromises((prevCount) => prevCount - 1);
    }
  }, [addOns, createPerformancePromises, dispatch, setIsAddOnLoading]);

  useEffect(() => {
    dispatch(
      actions.order.setIsFetchingPerformancesForAddOns(pendingPromises !== 0),
    );
  }, [pendingPromises]);

  // Avoid infinite loop of re-renders by only updating state when the current step changes
  useEffect(() => {
    if (currentStep !== currentStepState) {
      setCurrentStepState(currentStep);
    }
  }, [currentStep]);

  // Avoid infinite loop of re-renders by only updating state when the add on quantity changes
  useEffect(() => {
    if (shopCartAddonsWithQty.length !== addOnQtyState) {
      setAddOnQtyState(shopCartAddonsWithQty.length);
    }
  }, [shopCartAddonsWithQty.length]);

  // Get day performances for all stat groups
  // Watch for changes to shopCartAddons length. When an add on has quantity,
  // Check if it needs a performance and allocate
  useEffect(() => {
    asyncEffect();
  }, [asyncEffect, currentStepState]);

  return {
    isLoading: localLoadingState || dayAvailabilities.some((a) => a.isLoading),
  };
};
